import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Boxes, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function PartNameSettings() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/part-names");
      setParts(data.part_names || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load part names");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const name = value.trim();
    if (!name) return;
    setAdding(true);
    try {
      const { data } = await api.post("/part-names", { part_name: name });
      setParts(data.part_names || []);
      setValue("");
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add part name");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (name) => {
    setRemoving(name);
    try {
      const { data } = await api.delete(`/part-names/${encodeURIComponent(name)}`);
      setParts(data.part_names || []);
      toast.success(`Removed "${name}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove part name");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="border border-[#e6e5df] bg-white rounded-sm" data-testid="part-name-settings">
      <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
        <Boxes size={16} className="text-[#647265]" />
        <div className="font-display font-bold text-[#232826]">Part Names</div>
        <div className="ml-auto text-xs font-mono text-[#6b6a68]">{parts.length} part{parts.length !== 1 ? "s" : ""}</div>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-[#6b6a68]">These appear in the mandatory "Part name" dropdown on the New Request form. Staff can always choose "Other" to type one manually.</p>

        <div className="flex flex-wrap gap-2" data-testid="part-name-list">
          {loading && <div className="font-mono text-xs text-[#6b6a68]">Loading…</div>}
          {!loading && parts.map((p) => (
            <span key={p} data-testid={`part-name-chip-${p}`}
              className="inline-flex items-center gap-1.5 rounded-sm border border-[#cfcfc9] bg-[#efece5] px-2.5 py-1 text-sm text-[#232826]">
              {p}
              <button
                type="button"
                onClick={() => remove(p)}
                disabled={removing === p || parts.length <= 1}
                title={parts.length <= 1 ? "At least one part name must remain" : `Remove ${p}`}
                data-testid={`part-name-remove-${p}`}
                className="text-[#8a3a2a] hover:text-[#6a2a1e] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>

        <form onSubmit={add} className="flex gap-2 pt-1">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Add a new part name…"
            data-testid="part-name-input"
            className="bg-white border-[#cfcfc9] rounded-sm text-[#232826] max-w-xs"
          />
          <Button
            type="submit"
            disabled={adding || !value.trim()}
            data-testid="part-name-add-btn"
            className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest"
          >
            <Plus size={14} className="mr-1.5" /> {adding ? "Adding…" : "Add"}
          </Button>
        </form>
      </div>
    </div>
  );
}
